import { useState } from "react";
import type { ToolCallMessagePartComponent } from "@assistant-ui/react";
import { useThreadRuntime } from "@assistant-ui/react";
import { CheckIcon, Clock3Icon, MapPinIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { useT } from "@/locale-context";
import {
  updateConversationContext,
  type DealerLocation,
  type DealerLocationToolResult,
} from "@/lib/api";

const BRAND_DISPLAY: Record<string, string> = {
  VW: "VW",
  VWB: "VWB",
  Audi: "Audi",
  SEAT: "SEAT",
  CUPRA: "CUPRA",
  Skoda: "Škoda",
};

function displayBrand(code: string): string {
  return BRAND_DISPLAY[code] ?? code;
}

function isDealerLocationTool(
  toolName: string,
): toolName is "FindDealerLocations" | "ListDealerLocations" {
  return toolName === "FindDealerLocations" || toolName === "ListDealerLocations";
}

function isDealerLocationToolResult(value: unknown): value is DealerLocationToolResult {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.status === "string";
}

export const DealerLocationToolCard: ToolCallMessagePartComponent = ({
  toolName,
  result,
  status,
}) => {
  const t = useT();

  if (!isDealerLocationTool(toolName)) return null;

  if (status?.type === "running") {
    return (
      <div className="my-3 rounded-2xl border border-sky-200 bg-gradient-to-br from-slate-50 to-white p-4 shadow-sm">
        <div className="flex items-center gap-2">
          <div className="size-2 animate-pulse rounded-full bg-sky-500" />
          <p className="font-semibold text-neutral-900 text-sm">
            {t("dealer.searching")}
          </p>
        </div>
      </div>
    );
  }

  if (!isDealerLocationToolResult(result)) return null;

  if (result.status === "brand_not_found") {
    return (
      <div className="my-3 rounded-2xl border border-amber-200 bg-amber-50/70 p-4 shadow-sm">
        <p className="font-semibold text-amber-950 text-sm">{t("dealer.brandNotFound")}</p>
        <p className="mt-1 text-amber-900/80 text-xs leading-relaxed">
          {result.note ?? t("dealer.brandNotFoundHint")}
        </p>
        {!!result.availableBrands?.length && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {result.availableBrands.map((brand) => (
              <span
                key={brand}
                className="rounded-full border border-amber-200 bg-white px-2.5 py-1 text-[11px] text-neutral-700"
              >
                {brand}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (result.status === "error") {
    return (
      <div className="my-3 rounded-2xl border border-rose-200 bg-rose-50/70 p-4 shadow-sm">
        <p className="font-semibold text-rose-950 text-sm">{t("dealer.lookupFailed")}</p>
        <p className="mt-1 text-rose-900/80 text-xs leading-relaxed">
          {result.error ?? result.instruction ?? t("dealer.tryAgain")}
        </p>
      </div>
    );
  }

  const locations = result.locations ?? [];
  if (locations.length === 0) {
    return (
      <div className="my-3 rounded-2xl border border-slate-200 bg-slate-50/70 p-4 shadow-sm">
        <p className="font-semibold text-neutral-900 text-sm">{t("dealer.noLocations")}</p>
        <p className="mt-1 text-neutral-600 text-xs leading-relaxed">
          {result.note ?? t("dealer.noLocationsHint")}
        </p>
      </div>
    );
  }

  const title =
    toolName === "FindDealerLocations" ? t("dealer.nearestTitle") : t("dealer.listTitle");

  return (
    <div className="my-3 rounded-[26px] bg-[#e7edf6] p-3">
      <p className="mb-3 px-1 font-normal text-[#353a47] text-[16px] leading-[1.3]">
        {title}
      </p>

      <div className="flex flex-col gap-3">
        {locations.map((location) => (
          <DealerRow key={location.id} location={location} />
        ))}
      </div>
    </div>
  );
};

function DealerRow({ location }: { location: DealerLocation }) {
  const t = useT();
  const runtime = useThreadRuntime({ optional: true });
  const [pinning, setPinning] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pinAsPreferred = async () => {
    setError(null);
    setPinning(true);
    try {
      const win = window as Window & { __WILDSTREAM_CONVERSATION_ID__?: string };
      if (win.__WILDSTREAM_CONVERSATION_ID__) {
        await updateConversationContext({
          conversationId: win.__WILDSTREAM_CONVERSATION_ID__,
          preferredDealerId: location.id,
        });
      }
      runtime?.append({
        role: "user",
        content: [
          {
            type: "text",
            text: `→ Dealer selected: ${location.name}${location.city ? ` (${location.city})` : ""}`,
          },
        ],
      });
      setPinned(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("dealer.couldNotSave"));
    } finally {
      setPinning(false);
    }
  };

  const handleCardClick = () => {
    if (pinning || pinned) return;
    pinAsPreferred();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleCardClick();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      className={cn(
        "rounded-[8px] border border-[#d3dfe7] bg-white px-5 py-4 transition",
        pinned ? "cursor-default" : "cursor-pointer hover:border-[#7f95ad]",
      )}
    >
      <p className="flex items-center gap-1.5 font-bold text-[16px] text-[#353a47] leading-[1.3]">
        {location.name}
        {pinned ? (
          <CheckIcon
            className="size-4 shrink-0 text-emerald-600"
            aria-label={t("dealer.chosen")}
          />
        ) : null}
      </p>
      <p className="mt-0.5 text-[14px] text-[#353a47]/50 leading-[1.3]">
        {location.address}
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[14px] text-[#353a47] leading-[1.3]">
        <span className="inline-flex items-center gap-1">
          <MapPinIcon className="size-3.5" />
          {location.distanceKm != null
            ? `${location.distanceKm.toFixed(1)}km`
            : (location.city ?? location.postalCode ?? t("dealer.locationAvailable"))}
        </span>
        <span className="inline-flex items-center gap-1">
          <Clock3Icon className="size-3.5" />
          {location.openingHoursLabel ?? t("dealer.hoursUnavailable")}
        </span>
      </div>

      {location.brandCodes.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-[7px]">
          {location.brandCodes.map((code) => (
            <span
              key={code}
              className="inline-flex h-[27px] items-center rounded-full border border-[#d3dfe7] px-4 py-0.5 text-[12px] text-[#646464]"
            >
              {displayBrand(code)}
            </span>
          ))}
        </div>
      )}

      {location.serviceNotes && (
        <p className="mt-3 rounded-xl bg-slate-50 px-3 py-2 text-[11px] leading-relaxed text-slate-600">
          {location.serviceNotes}
        </p>
      )}

      {error && (
        <p className="mt-2 text-[11px] text-rose-700">{error}</p>
      )}
    </div>
  );
}
